import React from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Grid,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Chip,
} from "@material-ui/core";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import {
    downloadInvoiceCuttingDocument,
    fetchInvoiceCuttings,
    validateInvoiceCutting,
} from "redux/actions/invoices";
import CloseIcon from '@material-ui/icons/Close';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import SettingsIcon from '@material-ui/icons/Settings';
import DoneIcon from '@material-ui/icons/Done';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import { cloneDeep } from "lodash";
import PDFViewer from "pages/Invoices/Detail/Components/PDFViewer";
import PDFJSBackend from "pages/Invoices/Detail/Components/pdfjs";
import ImageWithMagnifier from "./ImageWithMagnifier";
import { convertPdfFilesToImages, InvoiceCuttingStatus } from "./utils";
import useStyles from "./style";


const onDragEnd = (result, rows, setRows) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
        const sourceColumn = rows[source.droppableId];
        const destColumn = rows[destination.droppableId];
        const sourceItems = [...sourceColumn.items];
        const destItems = [...destColumn.items];
        const [removed] = sourceItems.splice(source.index, 1);
        destItems.splice(destination.index, 0, removed);
        setRows({
            ...rows,
            [source.droppableId]: {
                ...sourceColumn,
                items: sourceItems
            },
            [destination.droppableId]: {
                ...destColumn,
                items: destItems
            }
        });
    } else {
        const column = rows[source.droppableId];
        const copiedItems = [...column.items];
        const [removed] = copiedItems.splice(source.index, 1);
        copiedItems.splice(destination.index, 0, removed);
        setRows({
            ...rows,
            [source.droppableId]: {
                ...column,
                items: copiedItems
            }
        });
    }
};

function InvoiceCuttingDialog({ open, onClose, invoiceCutting }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const [pagesList, setPagesList] = React.useState([]);
    const [rows, setRows] = React.useState({});
    const [selectedItem, setSelectedItem] = React.useState(null);
    const [magnifier, setMagnifier] = React.useState(false);
    const [invoicesCount, setInvoicesCount] = React.useState(null);
    const [documentBlob, setDocumentBlob] = React.useState(null);
    const documentData = useSelector(({ requests }) =>
        get(requests, "queries.DOWNLOAD_INVOICE_CUTTING_DOCUMENT.data")
    );
    const [showDocument, setShowDocument] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [prevInstance, setPrevInstance] = React.useState(null);

    React.useEffect(() => {
        setDocumentBlob(documentData);
    }, [documentData]);

    React.useEffect(() => {
        if (pagesList && invoiceCutting?.structure) {
            const rows_ = {};
            JSON.parse(invoiceCutting.structure).forEach(pages => {
                const name = Math.random().toString(36).slice(-5);
                rows_[name] = {
                    name,
                    items: pages.map(page => ({
                        id: String(page),
                        image: pagesList.find(p => p.id === page)?.image
                    }))
                }
            })
            setRows(rows_);
            setSelectedItem(Object.values(rows_)[0].items[0]);
            setLoading(false);
        }
    }, [pagesList]);

    React.useEffect(() => {
        if (documentBlob) {
            convertPdfFilesToImages([documentBlob]).then(files => setPagesList(files));
        }
    }, [documentBlob]);

    const handleClose = () => {
        onClose();
        setDocumentBlob(null);
        setSelectedItem(null);
        setRows({});
        setMagnifier(false);
        setShowDocument(false);
        setPrevInstance(null);
    };

    React.useEffect(() => () => handleClose(), []);

    React.useEffect(() => {
        if (invoiceCutting) {
            setLoading(true);
            dispatch(downloadInvoiceCuttingDocument(invoiceCutting.id, () => {
                if (invoiceCutting.status === InvoiceCuttingStatus.IN_PROGRESS) setLoading(false);
            }));
            setPrevInstance(invoiceCutting);
        } else if (prevInstance) handleClose();
    }, [invoiceCutting]);

    React.useEffect(() => {
        if (rows) {
            setInvoicesCount(Object.values(rows).reduce((count, v) => v.items.length > 0 ? (count + 1) : count, 0));
        }
    }, [rows]);

    const validate = () => {
        setLoading(true);
        const structure = Object.values(rows).map(row => row.items.map(item => Number(item.id))).filter(i => i.length > 0);
        const onSuccess = () => dispatch(fetchInvoiceCuttings()).then(() => setLoading(false));
        dispatch(validateInvoiceCutting(invoiceCutting.id, JSON.stringify(structure), onSuccess));
    };

    const addRow = (index) => {
        const rows_ = {};
        const newRow = {
            name: Math.random().toString(36).slice(-5),
            items: []
        };
        if (index === null) {
            rows_[newRow.name] = newRow;
        }
        let i = 0;
        // eslint-disable-next-line no-restricted-syntax
        for (const [name, row] of Object.entries(rows)) {
            rows_[name] = row;
            if (index === i) {
                rows_[newRow.name] = newRow;
            }
            i += 1;
        }
        setRows(rows_);
    };

    const removeRow = (name) => {
        const rows_ = cloneDeep(rows);
        delete rows_[name];
        setRows(rows_);
    };

    const renderAddRow = (index) => (
        invoiceCutting?.status === InvoiceCuttingStatus.PENDING_VALIDATION
            ? <Box display="flex" justifyContent="center">
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => addRow(index)}
                >
                    <AddBoxIcon />
                </IconButton>
            </Box>
            : <></>
    );

    const getStatusIcon = () => {
        switch (invoiceCutting?.status) {
            case InvoiceCuttingStatus.PENDING_VALIDATION:
                return <HourglassEmptyIcon />;
            case InvoiceCuttingStatus.IN_PROGRESS:
            case InvoiceCuttingStatus.VALIDATED:
                return <SettingsIcon className={classes.rotateIcon} />;
            default:
                return <></>;
        }
    };

    const getStatusLabel = () => {
        switch (invoiceCutting?.status) {
            case InvoiceCuttingStatus.IN_PROGRESS:
                return t("invoiceCutting.cutting-in-progress");
            case InvoiceCuttingStatus.PENDING_VALIDATION:
                return t("invoiceCutting.pending-validation");
            case InvoiceCuttingStatus.VALIDATED:
                return t("invoiceCutting.integration-in-progress");
            default:
                return <></>;
        }
    };

    const renderDocumentView = () => {
        if (documentBlob) {
            return <Grid container justify="center" style={{ height: "100%" }}>
                <Grid item xs={8}>
                    <PDFViewer
                        backend={PDFJSBackend}
                        src={URL.createObjectURL(new Blob([documentBlob], { type: "application/pdf" }))}
                        style={{ width: "100%", height: "100%" }}
                    />
                </Grid>
            </Grid>;
        }
        return <></>;
    };

    const renderValidationView = () => (
        <Box className={classes.contentRoot}>
            <Box className={classes.contentSection}>
                <DragDropContext
                    onDragEnd={result => onDragEnd(result, rows, setRows)}
                >
                    {renderAddRow(null)}
                    {Object.entries(rows).map(([name, row], index) => (
                        <div key={name}>
                            <Box
                                style={{
                                    display: "flex",
                                    marginBottom: invoiceCutting?.status === InvoiceCuttingStatus.VALIDATED ? "10px" : 0
                                }}
                            >
                                <Box>
                                    <Box className={classes.invoiceIndexBox}>
                                        {index + 1}
                                    </Box>
                                    {
                                        row.items.length === 0 &&
                                        <IconButton
                                            size="small"
                                            onClick={() => removeRow(name)}
                                            style={{ marginTop: "10px" }}
                                        >
                                            <DeleteForeverIcon color="error" />
                                        </IconButton>
                                    }
                                </Box>
                                <Droppable
                                    droppableId={name}
                                    direction="horizontal"
                                    justifyContent="center"
                                    alignContent="center"
                                >
                                    {(provided, snapshot) => (
                                        <Box
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className={classes.droppable}
                                            style={{ background: snapshot.isDraggingOver ? "lightblue" : "#E2E6EE" }}
                                        >
                                            {row.items.map((item, idx) => (
                                                <Draggable
                                                    key={item.id}
                                                    draggableId={item.id}
                                                    index={idx}
                                                    isDragDisabled={invoiceCutting?.status !== InvoiceCuttingStatus.PENDING_VALIDATION}
                                                >
                                                    {(provided2) => (
                                                        <div
                                                            ref={provided2.innerRef}
                                                            {...provided2.draggableProps}
                                                            {...provided2.dragHandleProps}
                                                            className={classes.draggable}
                                                            style={{
                                                                border: selectedItem?.id === item.id ? `2px solid green` : "none",
                                                                boxShadow: selectedItem?.id === item.id ? "none" : "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
                                                                ...provided2.draggableProps.style
                                                            }}
                                                        >
                                                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                                                            <img
                                                                src={item.image}
                                                                height="100%"
                                                                onClick={() => setSelectedItem(item)}
                                                                alt="Invoice page"
                                                            />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            )
                                            )}
                                            {provided.placeholder}
                                        </Box>
                                    )
                                    }
                                </Droppable>
                            </Box>
                            {renderAddRow(index)}
                        </div>
                    )
                    )}
                </DragDropContext>
            </Box>
            <Box className={classes.contentSection}>
                {
                    magnifier
                        ? <ImageWithMagnifier
                            src={selectedItem?.image}
                            className={classes.previewImage}
                        />
                        : <img
                            src={selectedItem?.image}
                            width="100%"
                            className={classes.previewImage}
                            alt="Selected invoice page"
                        />
                }
            </Box>
        </Box>
    );

    /* 
    NOTE: don't make renderContent() or the functions it returns as components,
    it will make them rerender each time some parent component rerenders,
    and this will create problems (e.g. scroll resets to 0 on rerender).
    */

    const renderContent = () => {
        switch (invoiceCutting?.status) {
            case InvoiceCuttingStatus.IN_PROGRESS:
                return renderDocumentView();
            case InvoiceCuttingStatus.PENDING_VALIDATION:
            case InvoiceCuttingStatus.VALIDATED:
                return showDocument ? renderDocumentView() : renderValidationView();
            default:
                return <></>;
        };
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            disableBackdropClick
            maxWidth="xl"
            classes={{
                paper: classes.dialogPaper
            }}
        >
            {
                loading &&
                <Box className={classes.loader}>
                    {t("loading")}
                    <span className={classes.loaderDot1}>.</span>
                    <span className={classes.loaderDot2}>.</span>
                    <span className={classes.loaderDot3}>.</span>
                </Box>
            }
            <DialogTitle>
                <Grid container justify="space-between" alignItems="flex-start">
                    <Grid item container xs spacing={2}>
                        <Grid item>
                            <Typography variant="h6">{t("invoiceCutting.title")}</Typography>
                        </Grid>
                        <Grid item>
                            <Chip
                                label={`${invoiceCutting?.pagesCount} pages`}
                                className={invoiceCutting?.status !== InvoiceCuttingStatus.IN_PROGRESS ? classes.pagesCountChip1 : classes.pagesCountChip2}
                            />
                            {
                                invoiceCutting?.status !== InvoiceCuttingStatus.IN_PROGRESS &&
                                <Chip
                                    label={`${invoicesCount} ${t("Invoices").toLowerCase()}`}
                                    className={classes.invoicesCountChip}
                                />
                            }
                        </Grid>
                        <Grid item>
                            <Chip
                                label={getStatusLabel()}
                                icon={getStatusIcon()}
                                variant="outlined"
                                color="primary"
                                className={classes.statusChip}
                            />
                        </Grid>
                    </Grid>
                    <Grid container item xs justify="flex-end" alignItems="center" spacing={2}>
                        {
                            invoiceCutting?.status === InvoiceCuttingStatus.PENDING_VALIDATION &&
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={validate}
                                    disableElevation
                                    startIcon={<DoneIcon />}
                                    disabled={loading}
                                >
                                    {t("invoiceCutting.validate")}
                                </Button>
                            </Grid>
                        }
                        {
                            [InvoiceCuttingStatus.PENDING_VALIDATION, InvoiceCuttingStatus.VALIDATED].includes(invoiceCutting?.status) &&
                            <>
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disableElevation
                                        style={{ textTransform: "none" }}
                                        onClick={() => setShowDocument(prev => !prev)}
                                        disabled={loading}
                                    >
                                        {showDocument ? t("invoiceCutting.show-cutting") : t("invoiceCutting.show-original-document")}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Tooltip title={t("invoiceCutting.activate-magnifier")}>
                                        <span>
                                            <IconButton
                                                onClick={() => setMagnifier((prev) => !prev)}
                                                className={classes.iconButton}
                                                size="small"
                                                color={magnifier ? "secondary" : "default"}
                                                disabled={loading}
                                            >
                                                <ZoomInIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </>
                        }
                        <Grid item>
                            <Tooltip title={t("close")}>
                                <IconButton
                                    onClick={handleClose}
                                    className={classes.iconButton}
                                    size="small"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent>
                {!loading && renderContent()}
            </DialogContent>
            <DialogActions />
        </Dialog>
    );
}

export default InvoiceCuttingDialog;